import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Papa from "papaparse";
import axios from "axios";
import { Download, Search, Eye } from "lucide-react";
import { Logo } from "../components/Logo";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SharedView() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, error: "", file: null });
    const [query, setQuery] = useState("");
    const { user } = useAuth();
    const isPaid = !!user && (user.is_paid || user.role === "admin");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/shared/${token}`);
                setState({ loading: false, error: "", file: data });
            } catch (e) {
                setState({ loading: false, error: e.response?.status === 404 ? "This share link is invalid or has been revoked." : "Could not load shared file.", file: null });
            }
        })();
    }, [token]);

    const parsed = useMemo(() => {
        if (!state.file?.content) return { headers: [], rows: [] };
        const out = Papa.parse(state.file.content, { skipEmptyLines: false });
        const data = out.data || [];
        if (data.length === 0) return { headers: [], rows: [] };
        const headers = (data[0] || []).map((h, i) => h || `Col ${i + 1}`);
        const rows = data.slice(1).map((r) => {
            const arr = Array.isArray(r) ? r.slice() : [];
            while (arr.length < headers.length) arr.push("");
            return arr.slice(0, headers.length);
        });
        return { headers, rows };
    }, [state.file]);

    const filteredRows = useMemo(() => {
        if (!query.trim()) return parsed.rows.map((r, i) => ({ r, i }));
        const q = query.toLowerCase();
        return parsed.rows.map((r, i) => ({ r, i })).filter(({ r }) => r.some((c) => String(c).toLowerCase().includes(q)));
    }, [parsed.rows, query]);

    const downloadCsv = () => {
        const blob = new Blob([state.file.content], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = state.file.name || "shared.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Helmet><title>{`${state.file?.name || "Shared CSV"} — OpenCSV`}</title></Helmet>
            <header className="border-b border-black/10 bg-white" data-testid="shared-navbar">
                <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
                    <Link to="/"><Logo /></Link>
                    {isPaid ? (
                        <Link to="/app" className="btn-brutal bg-cobalt text-white px-4 py-2 ui-heading text-sm font-semibold border border-cobalt" data-testid="shared-cta-open-editor">
                            Open the editor →
                        </Link>
                    ) : (
                        <Link to={user ? "/pricing" : "/signup"} className="btn-brutal bg-cobalt text-white px-4 py-2 ui-heading text-sm font-semibold border border-cobalt" data-testid="shared-cta-signup">
                            Try OpenCSV Pro →
                        </Link>
                    )}
                </div>
            </header>

            {state.loading && (
                <div className="min-h-[60vh] flex items-center justify-center text-ink-soft" data-testid="shared-loading">Loading shared file…</div>
            )}

            {state.error && (
                <div className="min-h-[60vh] flex items-center justify-center" data-testid="shared-error">
                    <div className="text-center max-w-md px-6">
                        <h1 className="font-display font-black tracking-tighter text-3xl">Hm, that link's not working.</h1>
                        <p className="mt-3 text-sm text-ink-soft">{state.error}</p>
                        <Link to="/" className="btn-brutal mt-6 inline-block bg-ink text-white px-5 py-2.5 ui-heading text-sm font-semibold">Back to home</Link>
                    </div>
                </div>
            )}

            {state.file && (
                <div className="mx-auto max-w-7xl px-4 md:px-8 py-6" data-testid="shared-content">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 border border-ink/15 ui-heading text-[11px] uppercase tracking-widest text-ink-soft">
                            <Eye size={12} /> Read only
                        </span>
                        <h1 className="font-mono text-base">{state.file.name}</h1>
                        <span className="text-xs text-ink-muted">{parsed.rows.length} rows · {parsed.headers.length} cols</span>
                        <div className="flex-1" />
                        <div className="relative">
                            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-muted" />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="pl-7 pr-3 py-2 border border-ink/20 bg-white focus-cobalt text-sm w-48" data-testid="shared-search-input" />
                        </div>
                        <button onClick={downloadCsv} className="btn-brutal inline-flex items-center gap-2 border border-ink/20 bg-white px-3 py-2 ui-heading text-sm" data-testid="shared-download-button">
                            <Download size={14} /> Download
                        </button>
                    </div>

                    <div className="border border-black/10 bg-white overflow-auto subtle-scroll max-h-[70vh]">
                        <table className="csv-grid w-full">
                            <thead>
                                <tr>
                                    <th className="row-num">#</th>
                                    {parsed.headers.map((h, ci) => <th key={ci} className="px-3 py-2 min-w-[140px]">{h}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map(({ r, i }) => (
                                    <tr key={i}>
                                        <td className="row-num">{i + 1}</td>
                                        {r.map((c, ci) => <td key={ci} className="px-3 py-1.5">{c}</td>)}
                                    </tr>
                                ))}
                                {filteredRows.length === 0 && (
                                    <tr><td colSpan={parsed.headers.length + 1} className="p-6 text-center text-ink-muted text-sm">No rows match your search.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isPaid && (
                        <div className="mt-8 border border-ink/15 bg-paper p-6 flex items-center justify-between flex-wrap gap-4" data-testid="shared-cta-banner">
                            <div>
                                <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ powered by</div>
                                <h2 className="font-display font-black tracking-tighter text-2xl mt-1">Like this? Edit your own CSVs with Pro.</h2>
                                <p className="text-sm text-ink-soft mt-1">OpenCSV is the lightest CSV editor on the web. Lifetime access — once.</p>
                            </div>
                            <Link to={user ? "/pricing" : "/signup"} className="btn-brutal bg-ink text-white px-5 py-2.5 ui-heading text-sm font-semibold border border-ink" data-testid="shared-cta-banner-buy">Try OpenCSV Pro</Link>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
